import { Add } from "@mui/icons-material";
import { Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

import { PROVIDER_DETAIL_STYLES } from "../../styles";

import ContactDraftRow from "./ContactDraftRow";

export default function ContactPanel({
  addButtonId,
  addTooltip,
  canAdd,
  canDeleteRows,
  canEditRows,
  disableAdd,
  displayItems,
  draftRows,
  emptyText,
  getDisplayLabel,
  getDisplayValue,
  getRowOptions,
  headerIcon,
  inputIdPrefix,
  isEditing,
  isLoading,
  onAdd,
  onRemoveDraft,
  onRowChange,
  panelTitle,
  selectIdPrefix,
  valueLabel,
}) {
  const hasDraftRows = draftRows.length > 0;
  const isAddDisabled = !canAdd || disableAdd;

  return (
    <div className="overflow-hidden rounded-[8px]" style={PROVIDER_DETAIL_STYLES.contactPanel}>
      <div className="flex items-center justify-between gap-3 px-4 py-3" style={PROVIDER_DETAIL_STYLES.contactPanelHeader}>
        <div className="flex min-w-0 items-center gap-2">{headerIcon}<Typography className="text-[15px] font-semibold" sx={PROVIDER_DETAIL_STYLES.contactPanelHeaderText}>{panelTitle}</Typography></div>
        {isEditing ? (
          <Tooltip title={addTooltip}>
            <span>
              <button
                type="button"
                id={addButtonId}
                className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold"
                style={isAddDisabled ? PROVIDER_DETAIL_STYLES.contactPanelAddButtonDisabled : PROVIDER_DETAIL_STYLES.contactPanelAddButton}
                disabled={isAddDisabled}
                onClick={onAdd}
              >
                Add
                <Add sx={isAddDisabled ? PROVIDER_DETAIL_STYLES.contactPanelAddIconDisabled : PROVIDER_DETAIL_STYLES.contactPanelAddIcon} />
              </button>
            </span>
          </Tooltip>
        ) : null}
      </div>
      <div className="max-h-[360px] overflow-y-auto p-4 pr-3 [&::-webkit-scrollbar-thumb]:bg-blue-700 [&::-webkit-scrollbar]:[width:6px]" style={PROVIDER_DETAIL_STYLES.contactPanelBody}>
        {isLoading ? (
          <Typography className="text-sm italic" sx={PROVIDER_DETAIL_STYLES.emptyValue}>Loading...</Typography>
        ) : !isEditing && !displayItems.length ? (
          <Typography className="text-sm italic" sx={PROVIDER_DETAIL_STYLES.emptyValue}>{emptyText}</Typography>
        ) : (
          <div className="flex flex-col gap-3">
            {!isEditing ? displayItems.map((item, index) => (
              <div key={item.id} className={`grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 pb-3 ${index !== displayItems.length - 1 ? "border-b" : ""}`} style={PROVIDER_DETAIL_STYLES.contactRowDivider}>
                <div className="rounded-md px-3 py-2 text-sm font-semibold text-center" style={PROVIDER_DETAIL_STYLES.contactLabelPill}>{getDisplayLabel(item)}</div>
                <Typography className="break-words text-sm leading-6" sx={PROVIDER_DETAIL_STYLES.fieldValue}>{getDisplayValue(item) || "Not available"}</Typography>
              </div>
            )) : null}
            {isEditing && !hasDraftRows ? <Typography className="text-sm italic" sx={PROVIDER_DETAIL_STYLES.emptyValue}>No rows to edit yet. Use Add to create one.</Typography> : null}
            {isEditing ? draftRows.map((row) => (
              <ContactDraftRow
                key={row.localId}
                canRemove={canDeleteRows || !row.id}
                disableFields={Boolean(row.id) && !canEditRows}
                row={row}
                options={getRowOptions(row)}
                valueLabel={valueLabel}
                onRowChange={onRowChange}
                onRemove={onRemoveDraft}
                selectIdPrefix={selectIdPrefix}
                inputIdPrefix={inputIdPrefix}
              />
            )) : null}
          </div>
        )}
      </div>
    </div>
  );
}

ContactPanel.propTypes = {
  addButtonId: PropTypes.string.isRequired,
  addTooltip: PropTypes.string.isRequired,
  canAdd: PropTypes.bool.isRequired,
  canDeleteRows: PropTypes.bool,
  canEditRows: PropTypes.bool,
  disableAdd: PropTypes.bool.isRequired,
  displayItems: PropTypes.array.isRequired,
  draftRows: PropTypes.array.isRequired,
  emptyText: PropTypes.string.isRequired,
  getDisplayLabel: PropTypes.func.isRequired,
  getDisplayValue: PropTypes.func.isRequired,
  getRowOptions: PropTypes.func.isRequired,
  headerIcon: PropTypes.node.isRequired,
  inputIdPrefix: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemoveDraft: PropTypes.func.isRequired,
  onRowChange: PropTypes.func.isRequired,
  panelTitle: PropTypes.string.isRequired,
  selectIdPrefix: PropTypes.string.isRequired,
  valueLabel: PropTypes.string.isRequired,
};

ContactPanel.defaultProps = {
  canDeleteRows: false,
  canEditRows: false,
};
